import React, { useEffect, useMemo, useState } from 'react';
import cx from 'classnames';

import Table from 'app/components/ui/Table';
import s from 'app/components/ui/Table/Table.module.sass';
import TokenName from 'app/components/ui/Table/components/TokenName';
import { AppRoutes } from 'app/static/routes/routes-enum';
import Button from 'app/components/ui/Button';
import BookmarkSvg from 'app/components/common/Bookmark';
import { GetPairsQuery } from 'app/generated/graphql';
import { QUIPUSWAP_LIQUIDITY } from 'app/utils/defaults';
import { getTokenLogo, transformMutezToTez } from 'app/utils/helpers';
import { useCurrency } from 'app/context/CurrencyContext';
import { CurrencyEnum } from 'app/components/ui/CurrencySwitcher/CurrencySwitcher';
import { ReactComponent as TezosSlim } from 'app/components/svg/TezosSlim.svg';

type PairsTableProps = {
  pageSize?: number
  data?: GetPairsQuery
  fetch: any
};

const PairsTable: React.FC<PairsTableProps> = ({
  pageSize,
  fetch,
  data,
}) => {
  const { currency, xtzUsdQuote } = useCurrency();

  const [pageCount, setPageCount] = useState<number>(0);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    if (data) {
      setPageCount(data.pairs?.totalCount!);
    }
  }, [data]);

  useEffect(() => {
    fetch({
      variables: {
        limit: pageSize ?? 10,
        offset,
      },
    });
  }, [fetch, offset, pageSize]);

  const columns = useMemo(() => [
    {
      Header: '#',
      id: 'index',
      accessor: (row: any, index: number) => (
        <div
          className={cx(s.cell)}
        >
          {offset + index + 1}
        </div>
      ),
    },
    {
      Header: 'Name',
      id: 'name',
      accessor: (row: any) => (
        <div
          className={cx(s.cell, s.alignLeft, s.nameWapper)}
        >
          <TokenName
            className={cx(s.pairsName)}
            firstTokenImage={
              row.node.token1.icon ? getTokenLogo(row.node.token1.icon) : null
            }
            secondTokenImage={
              row.node.token2.icon ? getTokenLogo(row.node.token2.icon) : null
            }
            name={`${row.node.token1.symbol ?? 'Unnamed'} / ${row.node.token2.symbol ?? 'Unnamed'}`}
            to={`${AppRoutes.PAIRS}/${row.node.id}`}
            tokensNames={{
              firstTokenName: row.node.token1.symbol ?? 'Unnamed',
              secondTokenName: row.node.token2.symbol ?? 'Unnamed',
            }}
            version={row.node.version}
          />
        </div>
      ),
    },
    {
      Header: 'Liquidity',
      id: 'liquidity',
      accessor: (row: any) => (
        <div
          className={cx(s.cell)}
        >
          {currency === CurrencyEnum.USD
            ? '$' : (
              <TezosSlim className={cx(s.tezos)} />
            )}
          {' '}
          {transformMutezToTez({ value: row.node.liquidity, currency, xtzUsdQuote })}
        </div>
      ),
    },
    {
      Header: 'Volume (24h)',
      id: 'volume24h',
      accessor: (row: any) => (
        <div
          className={cx(s.cell)}
        >
          {currency === CurrencyEnum.USD
            ? '$' : (
              <TezosSlim className={cx(s.tezos)} />
            )}
          {' '}
          {transformMutezToTez({ value: row.node.volume24h, currency, xtzUsdQuote })}
        </div>
      ),
    },
    {
      Header: 'Volume (7d)',
      id: 'volume7d',
      accessor: (row: any) => (
        <div
          className={cx(s.cell)}
        >
          {currency === CurrencyEnum.USD
            ? '$' : (
              <TezosSlim className={cx(s.tezos)} />
            )}
          {' '}
          {transformMutezToTez({ value: row.node.volume7d, currency, xtzUsdQuote })}
        </div>
      ),
    },
    {
      Header: 'Fees (24h)',
      id: 'volumeChange',
      accessor: (row: any) => (
        <div
          className={cx(s.cell)}
        >
          {currency === CurrencyEnum.USD
            ? '$' : (
              <TezosSlim className={cx(s.tezos)} />
            )}
          {' '}
          {transformMutezToTez({ value: row.node.volume24h * 0.003, currency, xtzUsdQuote })}
        </div>
      ),
    },
    {
      Header: 'Add liquidity',
      id: 'addLiquidity',
      accessor: (row: any) => {
        const tokenId: string = `${row.node.token2.standard === 'FA2' ? `_${row.node.token2.tokenId}` : ''}`;

        return (
          <div
            className={cx(s.cell)}
          >
            <Button sizeT="medium" href={`${QUIPUSWAP_LIQUIDITY}${row.node.token2.id}${tokenId}`}>
              + Add liquidity
            </Button>
          </div>
        );
      },
    },
    {
      Header: '+',
      id: 'save',
      accessor: (row: any) => {
        const currentItem = data?.pairs?.edges.find(
          (item: any) => item.node.id === row.node.id,
        )?.node?.id;

        return (
          <div
            className={cx(s.cell)}
          >
            <BookmarkSvg
              id={currentItem!}
              field="pairs"
              className={cx(s.bookmark)}
            />
          </div>
        );
      },
    },
  ], [currency, data?.pairs?.edges, offset, xtzUsdQuote]);

  return (
    <Table
      columns={columns}
      data={data?.pairs?.edges ?? []}
      pageCount={pageCount}
      theme="pairs"
      pageSize={pageSize ?? 10}
      setOffset={setOffset}
    />
  );
};

export default PairsTable;
